import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiGet, apiPost, ApiError } from "./api";
import type { OverviewData, ProgramData } from "./types";

export interface Session {
  state: "active" | "read_only" | "closed" | "pending" | "none";
  email: string;
  partner: {
    id: string;
    displayName: string;
    kind: string;
    status: string;
    notifyEarnings: boolean;
    website: string | null;
    socials: string[];
    primaryCode: string | null;
    /** Support changes these; older API versions omit them. */
    legalName?: string;
    country?: string | null;
  } | null;
}

/**
 * A signed-out (401) or refused (403) session is not refetched on window focus: the refetch would
 * swap the sign-in form for the loading state and lose what was typed. Signing in invalidates it.
 */
export function refetchSessionOnFocus(query: {
  state: { error: unknown };
}): boolean {
  const error = query.state.error;
  return !(
    error instanceof ApiError &&
    (error.status === 401 || error.status === 403)
  );
}

export const useSession = () =>
  useQuery({
    queryKey: ["session"],
    queryFn: () => apiGet<Session>("/api/partner/session"),
    retry: false,
    refetchOnWindowFocus: refetchSessionOnFocus,
  });

/**
 * A cache key: one name (`"overview"`), or a name and the query string's values
 * (`["performance", 30]`). `useWrite`'s invalidation by name matches both.
 */
export type QueryKey = string | readonly [string, ...(string | number)[]];

export const queryKeyOf = (key: QueryKey): readonly unknown[] =>
  typeof key === "string" ? [key] : key;

export const useGet = <T>(key: QueryKey, path: string) =>
  useQuery({ queryKey: queryKeyOf(key), queryFn: () => apiGet<T>(path) });

/**
 * The program's attribution window in days (`program.windowDays`), shared with
 * the Rates page's cache entry; null while it loads or when the read fails, so
 * copy can say "your attribution window" instead of a number.
 */
export function useWindowDays(): number | null {
  const program = useGet<ProgramData>("program", "/api/partner/program");
  return program.data?.windowDays ?? null;
}

/**
 * How many steps the partner still owes before a payout (a payout method, a
 * needed tax form), from `overview.setup`. 0 while the overview loads, fails,
 * or predates the `setup` field. Shares the Overview page's cache entry.
 */
export function useSetupStepsLeft(): number {
  const overview = useGet<OverviewData>("overview", "/api/partner/overview");
  return setupStepsLeft(overview.data);
}

export function setupStepsLeft(data: OverviewData | undefined): number {
  const setup = data?.setup;
  if (!setup) return 0;
  return (setup.payoutMethod ? 0 : 1) + (setup.taxFormNeeded ? 1 : 0);
}

/** A write that refreshes the named queries on success. */
export function useWrite<T, I>(path: string, invalidate: string[]) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: I) => apiPost<T>(path, input),
    onSuccess: () =>
      Promise.all(
        invalidate.map((k) => client.invalidateQueries({ queryKey: [k] })),
      ),
  });
}
